/**
 * CLUB LAPAROZ — My Account (<cl-account>)
 *
 * Security model
 * - The account server (Cloudflare Worker) checks passwords (stored only as PBKDF2 hashes)
 *   and owns the session. Bookings appear only after the customer links them with the
 *   booking reference + the mobile number used to book. This script never decides who
 *   is logged in: every view is loaded from an authenticated endpoint, and a 401 from the
 *   server always returns to the login screen.
 * - The session is an HttpOnly cookie set by the server. Where a browser blocks that
 *   cross-site cookie, the server also returns an opaque session token which is kept in
 *   sessionStorage purely as a transport (sent as a Bearer header); it is revocable on
 *   logout and is validated by the server on every request.
 * - All server data is written with textContent / attributes (never innerHTML).
 */
(() => {
  if (window.customElements.get('cl-account')) return;

  const TOKEN_KEY = 'clp_account_token';
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const ICONS = {
    arrow: 'M5 12h14M13 6l6 6-6 6',
    back: 'M19 12H5M11 18l-6-6 6-6',
    home: 'M3.5 10.5 12 4l8.5 6.5V19a1.5 1.5 0 0 1-1.5 1.5h-4.5V14h-5v6.5H5A1.5 1.5 0 0 1 3.5 19z',
    ticket: 'M4 7.5A1.5 1.5 0 0 1 5.5 6h13A1.5 1.5 0 0 1 20 7.5V10a2 2 0 0 0 0 4v2.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 16.5V14a2 2 0 0 0 0-4zM14 6v12',
    card: 'M3.5 7A1.5 1.5 0 0 1 5 5.5h14A1.5 1.5 0 0 1 20.5 7v10a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 17zM3.5 10h17M7 15h4',
    user: 'M12 12.3a3.8 3.8 0 1 0 0-7.6 3.8 3.8 0 0 0 0 7.6zM4.5 20c1.2-3.6 4.1-5.5 7.5-5.5s6.3 1.9 7.5 5.5',
    logout: 'M14 4.5h3.5A1.5 1.5 0 0 1 19 6v12a1.5 1.5 0 0 1-1.5 1.5H14M10 16l-4-4 4-4M6 12h9',
    check: 'm5.5 12.5 4 4 9-9',
    compass: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM15.5 8.5l-2 5-5 2 2-5z',
    download: 'M12 4v11m0 0-4.5-4.5M12 15l4.5-4.5M4.5 19.5h15',
    chat: 'M12 3.2a8.8 8.8 0 0 0-7.6 13.2L3.2 20.8l4.5-1.2A8.8 8.8 0 1 0 12 3.2Z',
    mail: 'M3.5 6.5h17v11h-17zM3.5 7l8.5 6.5L20.5 7',
    repeat: 'M17 2.5 20.5 6 17 9.5M3.5 11V9.5A3.5 3.5 0 0 1 7 6h13.5M7 21.5 3.5 18 7 14.5M20.5 13v1.5a3.5 3.5 0 0 1-3.5 3.5H3.5',
    shield: 'M12 3 4.5 6v5.5c0 4.6 3.2 8.3 7.5 9.5 4.3-1.2 7.5-4.9 7.5-9.5V6zM8.8 12l2.2 2.2 4.2-4.4',
  };

  function icon(name, size) {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.9');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    if (size) { svg.setAttribute('width', size); svg.setAttribute('height', size); }
    const p = document.createElementNS(SVG_NS, 'path');
    p.setAttribute('d', ICONS[name] || '');
    svg.appendChild(p);
    return svg;
  }

  function ornament() {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 64 12');
    svg.setAttribute('class', 'cla-ornament');
    svg.setAttribute('aria-hidden', 'true');
    svg.innerHTML = '<line x1="0" y1="6" x2="24" y2="6" stroke="currentColor"/><path d="M32 1 L37 6 L32 11 L27 6 Z" fill="currentColor"/><line x1="40" y1="6" x2="64" y2="6" stroke="currentColor"/>';
    return svg;
  }

  /** Tiny DOM builder. Strings become text nodes — never HTML. */
  function h(tag, attrs, ...children) {
    const el = document.createElement(tag);
    if (attrs) {
      for (const [k, v] of Object.entries(attrs)) {
        if (v == null || v === false) continue;
        if (k === 'class') el.className = v;
        else if (k === 'text') el.textContent = v;
        else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2), v);
        else if (k === 'dataset') Object.assign(el.dataset, v);
        else if (v === true) el.setAttribute(k, '');
        else el.setAttribute(k, String(v));
      }
    }
    for (const c of children.flat()) {
      if (c == null || c === false) continue;
      el.appendChild(typeof c === 'string' || typeof c === 'number' ? document.createTextNode(String(c)) : c);
    }
    return el;
  }

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }).format(d);
  };
  const fmtMoney = (paise, currency) => {
    const n = Number(paise || 0) / 100;
    try {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR', maximumFractionDigits: n % 1 ? 2 : 0 }).format(n);
    } catch (e) {
      return `₹${n.toLocaleString('en-IN')}`;
    }
  };
  const STATUS_LABEL = {
    confirmed: 'Confirmed', pending: 'Pending', cancelled: 'Cancelled', completed: 'Completed',
    paid: 'Paid in full', partially_paid: 'Partially paid', unpaid: 'Awaiting payment', refunded: 'Refunded', successful: 'Successful',
  };
  const METHOD_LABEL = { upi: 'UPI', card: 'Card', netbanking: 'Net banking', wallet: 'Wallet', emi: 'EMI', paylater: 'Pay later', bank_transfer: 'Bank transfer' };
  const badge = (status) => h('span', { class: `cla-badge cla-badge--${status}`, text: STATUS_LABEL[status] || status });

  class ApiError extends Error {
    constructor(status, code, message, extra) {
      super(message);
      this.status = status;
      this.code = code;
      this.extra = extra || {};
    }
  }

  class ClAccount extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.ac = new AbortController();
      this.root = this.querySelector('[data-cla-root]');
      this.apiBase = (this.dataset.apiBase || '').replace(/\/+$/, '');
      this.exploreUrl = this.dataset.exploreUrl || '/';
      this.whatsapp = this.dataset.whatsapp || '';
      this.supportEmail = this.dataset.supportEmail || '';
      this.defaultCc = this.dataset.defaultCc || '+91';
      this.customer = null;
      this.timers = new Set();
      this.imageCache = new Map();
      window.addEventListener('popstate', () => this.renderRoute(), { signal: this.ac.signal });
      this.start();
    }

    disconnectedCallback() {
      this._connected = false;
      this.ac.abort();
      this.clearTimers();
    }

    /* ------------------------------ helpers ------------------------------ */
    clearTimers() {
      for (const t of this.timers) clearInterval(t);
      this.timers.clear();
    }

    getToken() {
      try { return sessionStorage.getItem(TOKEN_KEY) || ''; } catch (e) { return ''; }
    }
    setToken(t) {
      try {
        if (t) sessionStorage.setItem(TOKEN_KEY, t);
        else sessionStorage.removeItem(TOKEN_KEY);
      } catch (e) { /* storage unavailable: cookie session still works */ }
    }

    async api(path, { method = 'GET', body } = {}) {
      if (!this.apiBase) throw new ApiError(0, 'not_configured', 'The account server is not configured yet.');
      const headers = { Accept: 'application/json' };
      if (body) headers['Content-Type'] = 'application/json';
      const token = this.getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
      let res;
      try {
        res = await fetch(`${this.apiBase}${path}`, {
          method,
          headers,
          body: body ? JSON.stringify(body) : undefined,
          credentials: 'include',
          cache: 'no-store',
          signal: this.ac.signal,
        });
      } catch (e) {
        if (e.name === 'AbortError') throw e;
        throw new ApiError(0, 'network', 'We could not reach the server. Check your connection and try again.');
      }
      let data = null;
      try { data = await res.json(); } catch (e) { /* non-JSON */ }
      if (!res.ok || !data || data.ok === false) {
        const err = (data && data.error) || {};
        const { code, message, ...extra } = err;
        throw new ApiError(res.status, code || 'error', message || 'Something went wrong. Please try again.', extra);
      }
      return data;
    }

    /** Any 401 means the server no longer recognises the session. */
    handleAuthError(e) {
      if (e instanceof ApiError && e.status === 401) {
        this.customer = null;
        this.setToken('');
        this.setUrl({}, true);
        this.renderLogin({ alert: { type: 'info', text: e.message || 'Please log in to continue.' } });
        return true;
      }
      return false;
    }

    params() {
      const p = new URLSearchParams(location.search);
      return { tab: p.get('tab') || '', ref: p.get('ref') || '' };
    }

    setUrl({ tab, ref }, replace) {
      const url = new URL(location.href);
      url.searchParams.delete('tab');
      url.searchParams.delete('ref');
      if (tab) url.searchParams.set('tab', tab);
      if (ref) url.searchParams.set('ref', ref);
      if (url.href === location.href) return;
      history[replace ? 'replaceState' : 'pushState']({}, '', url);
    }

    go(tab, ref) {
      this.setUrl({ tab, ref }, false);
      this.renderRoute();
      const top = this.getBoundingClientRect().top + window.scrollY - 20;
      if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
    }

    mount(node) {
      this.clearTimers();
      this.root.replaceChildren(node);
    }

    /* ------------------------------- start ------------------------------- */
    async start() {
      if (!this.apiBase) {
        this.renderLogin({ alert: { type: 'error', text: 'The account server URL is not set. Add it in the theme editor (CLUB LAPAROZ account → Account server URL).' } });
        return;
      }
      try {
        const data = await this.api('/api/account/me');
        this.customer = data.customer;
        this.renderRoute();
      } catch (e) {
        if (e.name === 'AbortError') return;
        if (e.status === 401 || e.status === 0 || e.status === 404) {
          this.setToken('');
          this.renderLogin(e.status === 0 ? { alert: { type: 'error', text: e.message } } : {});
        } else {
          this.renderLogin({ alert: { type: 'error', text: e.message } });
        }
      }
    }

    renderRoute() {
      if (!this.customer) {
        this.renderLogin();
        return;
      }
      const { tab, ref } = this.params();
      if (tab === 'bookings' && ref) return this.renderBookingDetail(ref);
      if (tab === 'bookings') return this.renderBookings();
      if (tab === 'payments') return this.renderPayments();
      if (tab === 'profile') return this.renderProfile();
      return this.renderOverview();
    }

    /* =============================== LOGIN =============================== */
    /** Auth screens: 'login' | 'register' | 'forgot'. this.auth keeps the typed email (never the password). */
    renderLogin(opts = {}) {
      this.auth = Object.assign({ mode: 'login', email: '' }, this.auth || {});
      if (opts.mode) this.auth.mode = opts.mode;
      const mode = this.auth.mode;
      const titles = {
        login: [this.dataset.loginHeading || 'WELCOME TO CLUB LAPAROZ', this.dataset.loginSubtitle || 'Your bookings, payments and experiences — all in one place.'],
        register: ['CREATE YOUR ACCOUNT', 'It takes less than a minute.'],
        forgot: ['FORGOT YOUR PASSWORD?', "No problem — we'll reset it for you."],
      };
      const [title, sub] = titles[mode] || titles.login;
      const card = h('div', { class: 'cla-card cla-login' },
        h('p', { class: 'cla-eyebrow', text: 'My account' }),
        h('h1', { class: 'cla-h1', id: 'cla-login-title', text: title }),
        h('p', { class: 'cla-sub', text: sub }),
        ornament());
      if (mode === 'login' || mode === 'register') {
        const tab = (m, label) => h('button', {
          type: 'button', role: 'tab', class: `cla-tab${mode === m ? ' is-active' : ''}`, 'aria-selected': mode === m ? 'true' : 'false',
          onclick: () => { if (mode !== m) this.renderLogin({ mode: m }); },
        }, label);
        card.appendChild(h('div', { class: 'cla-tabs', role: 'tablist', 'aria-label': 'Account' }, tab('login', 'Log in'), tab('register', 'Create account')));
      }
      const alertBox = h('div', { class: 'cla-alert', role: 'alert', hidden: true });
      if (opts.alert) this.showAlert(alertBox, opts.alert.text, opts.alert.type);
      const forms = { login: 'loginForm', register: 'registerForm', forgot: 'forgotPanel' };
      card.appendChild(this[forms[mode] || 'loginForm'](alertBox));
      card.appendChild(h('p', { class: 'cla-note' }, icon('shield', 14), ' Your password is encrypted and never shared.'));
      this.mount(h('div', { class: 'cla-login-wrap' }, card));
      const first = card.querySelector('form input:not([type=hidden]):not([readonly])');
      if (first && opts.focus !== false && (document.activeElement === document.body || !this.contains(document.activeElement))) first.focus({ preventScroll: true });
    }

    showAlert(box, text, type = 'error') {
      if (!box) return;
      box.className = `cla-alert${type === 'ok' ? ' cla-alert--ok' : type === 'info' ? ' cla-alert--info' : ''}`;
      box.setAttribute('role', type === 'error' ? 'alert' : 'status');
      box.textContent = text;
      box.hidden = !text;
    }

    /** Builds a labelled input with its own error line. */
    field(id, label, attrs = {}, hint) {
      const errId = `cla-${id}-err`;
      const input = h('input', Object.assign({ class: 'cla-input', id: `cla-${id}`, name: id, 'aria-describedby': errId }, attrs));
      const err = h('p', { class: 'cla-err', id: errId });
      input.addEventListener('input', () => { input.removeAttribute('aria-invalid'); err.textContent = ''; });
      const el = h('div', { class: 'cla-field' },
        h('label', { class: 'cla-label', for: `cla-${id}`, text: label }),
        attrs.type === 'password' ? this.passwordWrap(input) : input,
        hint ? h('p', { class: 'cla-hint', text: hint }) : null,
        err);
      return { el, input, err, set: (msg) => { input.setAttribute('aria-invalid', 'true'); err.textContent = msg; } };
    }

    passwordWrap(input) {
      const btn = h('button', { type: 'button', class: 'cla-reveal', 'aria-label': 'Show password', 'aria-pressed': 'false', text: 'Show' });
      btn.addEventListener('click', () => {
        const show = input.type === 'password';
        input.type = show ? 'text' : 'password';
        btn.textContent = show ? 'Hide' : 'Show';
        btn.setAttribute('aria-label', show ? 'Hide password' : 'Show password');
        btn.setAttribute('aria-pressed', show ? 'true' : 'false');
      });
      return h('div', { class: 'cla-pw' }, input, btn);
    }

    validEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v).trim()); }

    /** Mirrors the server rule so most mistakes are caught instantly; the server re-checks. */
    passwordProblem(pw) {
      if (pw.length < 8) return 'Use at least 8 characters.';
      if (!/[A-Za-z]/.test(pw) || !/\d/.test(pw)) return 'Use at least one letter and one number.';
      return '';
    }

    /** Routes field-level server errors to the matching input, everything else to the alert. */
    showServerError(ex, alertBox, fieldsByCode) {
      const f = fieldsByCode[ex.code];
      if (f) f.set(ex.message);
      else if (ex.extra && ex.extra.fields) {
        let shown = false;
        for (const [k, msg] of Object.entries(ex.extra.fields)) { if (fieldsByCode[k]) { fieldsByCode[k].set(msg); shown = true; } }
        if (!shown) this.showAlert(alertBox, ex.message);
      } else this.showAlert(alertBox, ex.message);
    }

    loginForm(alertBox) {
      const email = this.field('email', 'Email address', { type: 'email', autocomplete: 'email', inputmode: 'email', maxlength: '120', required: true, value: this.auth.email });
      const pw = this.field('password', 'Password', { type: 'password', autocomplete: 'current-password', maxlength: '128', required: true });
      const btn = this.button('LOG IN', { arrow: true, type: 'submit' });
      const forgot = h('button', { type: 'button', class: 'cla-link', text: 'Forgot password?' });
      forgot.addEventListener('click', () => { this.auth.email = email.input.value.trim(); this.renderLogin({ mode: 'forgot' }); });
      const form = h('form', { novalidate: true }, alertBox, email.el, pw.el, h('div', { class: 'cla-row-end' }, forgot), btn);
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (this.busy) return;
        const em = email.input.value.trim();
        if (!this.validEmail(em)) { email.set('Please enter a valid email address.'); email.input.focus(); return; }
        if (!pw.input.value) { pw.set('Please enter your password.'); pw.input.focus(); return; }
        this.showAlert(alertBox, '');
        this.auth.email = em;
        this.setBusy(btn, true);
        try {
          const data = await this.api('/api/auth/login', { method: 'POST', body: { email: em, password: pw.input.value } });
          await this.finishLogin(data);
        } catch (ex) {
          if (ex.name === 'AbortError') return;
          pw.input.value = '';
          this.showServerError(ex, alertBox, { invalid_email: email });
        } finally {
          this.setBusy(btn, false);
        }
      });
      return form;
    }

    registerForm(alertBox) {
      const name = this.field('name', 'Full name', { autocomplete: 'name', maxlength: '80', required: true });
      const email = this.field('email', 'Email address', { type: 'email', autocomplete: 'email', inputmode: 'email', maxlength: '120', required: true, value: this.auth.email });
      const pw = this.field('password', 'Create password', { type: 'password', autocomplete: 'new-password', maxlength: '128', required: true },
        'At least 8 characters, with a letter and a number.');
      const btn = this.button('CREATE ACCOUNT', { arrow: true, type: 'submit' });
      const form = h('form', { novalidate: true }, alertBox, name.el, email.el, pw.el, btn);
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (this.busy) return;
        const nm = name.input.value.trim();
        const em = email.input.value.trim();
        const p = pw.input.value;
        let bad = false;
        if (nm.length < 2) { name.set('Please enter your full name.'); bad = true; }
        if (!this.validEmail(em)) { email.set('Please enter a valid email address.'); bad = true; }
        const pp = this.passwordProblem(p);
        if (pp) { pw.set(pp); bad = true; }
        if (bad) { (form.querySelector('[aria-invalid="true"]') || name.input).focus(); return; }
        this.showAlert(alertBox, '');
        this.auth.email = em;
        this.setBusy(btn, true);
        try {
          const data = await this.api('/api/auth/register', { method: 'POST', body: { name: nm, email: em, password: p } });
          await this.finishLogin(data);
        } catch (ex) {
          if (ex.name === 'AbortError') return;
          if (ex.code === 'account_exists') {
            this.renderLogin({ mode: 'login', alert: { type: 'info', text: ex.message } });
            return;
          }
          this.showServerError(ex, alertBox, { invalid_name: name, invalid_email: email, weak_password: pw });
        } finally {
          this.setBusy(btn, false);
        }
      });
      return form;
    }

    /** Passwords are reset by the team (nothing is emailed), so this panel points to support. */
    forgotPanel(alertBox) {
      const msg = `Hi Club Laparoz! Please reset the password for my account: ${this.auth.email || ''}`.trim();
      const wrap = h('div', { class: 'cla-forgot' },
        alertBox,
        h('p', { class: 'cla-forgot__text', text: 'Message us from the mobile number or email on your account and our team will send you a temporary password. You can change it in My Profile after logging in.' }),
        this.whatsapp ? h('a', { class: 'cla-btn cla-btn--block', href: `https://wa.me/${this.whatsapp}?text=${encodeURIComponent(msg)}`, target: '_blank', rel: 'noopener' }, icon('chat'), h('span', { text: 'WHATSAPP US' })) : null,
        this.supportEmail ? h('a', { class: 'cla-btn cla-btn--ghost cla-btn--block', href: `mailto:${this.supportEmail}?subject=${encodeURIComponent('Password reset')}&body=${encodeURIComponent(msg)}` }, icon('mail'), h('span', { text: 'EMAIL US' })) : null,
        h('div', { class: 'cla-row-center' }, h('button', { type: 'button', class: 'cla-link', text: 'Back to log in', onclick: () => this.renderLogin({ mode: 'login' }) })));
      return wrap;
    }

    /** Stores the transport token, then confirms the session with the server before showing anything personal. */
    async finishLogin(data, message) {
      if (data.token) this.setToken(data.token);
      try {
        const me = await this.api('/api/account/me');
        this.customer = me.customer;
      } catch (ex) {
        this.setToken('');
        if (ex.status === 401) throw new ApiError(0, 'cookie_blocked', 'You were signed in, but your browser blocked the login session. Please allow cookies for this site and try again.');
        throw ex;
      }
      this.auth = null;
      this.setUrl({ tab: 'overview' }, true);
      this.renderOverview();
      if (message) {
        const main = this.querySelector('.cla-main');
        if (main) { const box = h('div', { class: 'cla-alert' }); this.showAlert(box, message, 'ok'); main.prepend(box); }
      }
    }

    button(label, { arrow, type = 'button', cls = '' } = {}) {
      return h('button', { type, class: `cla-btn cla-btn--block ${cls}`.trim() },
        h('span', { class: 'cla-btn__label', text: label }),
        arrow ? icon('arrow') : null);
    }

    setBusy(btn, busy) {
      this.busy = busy;
      if (!btn) return;
      btn.disabled = busy;
      btn.classList.toggle('is-loading', busy);
      btn.setAttribute('aria-busy', busy ? 'true' : 'false');
      const sp = btn.querySelector('.cla-btn__spinner');
      if (busy && !sp) btn.appendChild(h('span', { class: 'cla-btn__spinner', 'aria-hidden': 'true' }));
      if (!busy && sp) sp.remove();
    }

    /* ============================ DASHBOARD SHELL ============================ */
    shell(tab, content) {
      const c = this.customer;
      const item = (key, label, ic) => h('li', null,
        h('a', {
          href: `?tab=${key}`, 'aria-current': tab === key ? 'page' : null,
          onclick: (e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); this.go(key); },
        }, icon(ic), h('span', { text: label })));
      const side = h('aside', { class: 'cla-card cla-side', 'aria-label': 'Account navigation' },
        h('div', { class: 'cla-side__who' },
          h('p', { class: 'cla-side__name', text: c.name || 'CLUB LAPAROZ member' }),
          h('p', { class: 'cla-side__phone' }, icon('shield', 13), ` ${c.email}`)),
        h('ul', { class: 'cla-nav', role: 'list' },
          item('overview', 'Overview', 'home'),
          item('bookings', 'My Bookings', 'ticket'),
          item('payments', 'Payments & Subscriptions', 'card'),
          item('profile', 'My Profile', 'user'),
          h('li', { class: 'cla-nav__sep', 'aria-hidden': 'true' }),
          h('li', null, h('button', { type: 'button', onclick: () => this.logout() }, icon('logout'), h('span', { text: 'Logout' })))));
      return h('div', { class: 'cla-dash' }, side, h('div', { class: 'cla-main' }, content));
    }

    pageHead(eyebrow, title, sub, back) {
      return h('header', { class: 'cla-head' },
        back ? h('div', { class: 'cla-back' }, h('a', { class: 'cla-link', href: back.href, onclick: (e) => { e.preventDefault(); back.go(); } }, icon('back', 16), back.label)) : null,
        h('p', { class: 'cla-eyebrow', text: eyebrow }),
        h('h1', { class: 'cla-h1', text: title }),
        sub ? h('p', { class: 'cla-sub', text: sub }) : null);
    }

    skeleton(n = 2) {
      return h('div', { class: 'cla-list', role: 'status', 'aria-label': 'Loading' }, Array.from({ length: n }, () => h('div', { class: 'cla-skel' })));
    }

    errorCard(message, retry) {
      return h('div', { class: 'cla-card cla-empty' },
        h('p', { class: 'cla-alert', role: 'alert', text: message }),
        retry ? h('button', { type: 'button', class: 'cla-btn cla-btn--ghost', onclick: retry, text: 'Try again' }) : null);
    }

    emptyCard(ic, title, text, cta) {
      return h('div', { class: 'cla-card cla-empty' },
        h('span', { class: 'cla-empty__icon' }, icon(ic)),
        h('h2', { class: 'cla-h2', text: title }),
        h('p', { text }),
        cta ? h('a', { class: 'cla-btn', href: cta.href }, h('span', { text: cta.label }), icon('arrow')) : null);
    }

    /* ------------------------------ Overview ------------------------------ */
    renderOverview() {
      const c = this.customer;
      const name = (c.first_name || c.name || '').toUpperCase();
      const tile = (key, ic, title, text) => h('li', null,
        h('a', { class: 'cla-card cla-tile', href: `?tab=${key}`, onclick: (e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); this.go(key); } },
          h('span', { class: 'cla-tile__icon' }, icon(ic)),
          h('h2', { class: 'cla-h3', text: title }),
          h('p', { text }),
          h('span', { class: 'cla-tile__more' }, 'View details', icon('arrow'))));
      const content = h('div', null,
        this.pageHead('My account', name ? `WELCOME BACK, ${name}` : 'WELCOME BACK', this.dataset.dashSubtitle || 'Everything connected to your CLUB LAPAROZ experiences.'),
        !c.name ? h('p', { class: 'cla-alert cla-alert--info' }, 'Add your name and mobile number in ', h('a', { class: 'cla-link', href: '?tab=profile', onclick: (e) => { e.preventDefault(); this.go('profile'); }, text: 'My Profile' }), ' so we can personalise your account.') : null,
        h('ul', { class: 'cla-tiles', role: 'list' },
          tile('bookings', 'ticket', 'MY BOOKINGS', 'Your upcoming and past trips and workshops, with traveller and booking details.'),
          tile('payments', 'card', 'PAYMENTS & SUBSCRIPTIONS', 'Payment history, advance balances and any subscriptions.'),
          tile('profile', 'user', 'MY PROFILE', 'Your name, email, mobile number and city.')));
      this.mount(this.shell('overview', content));
    }

    /* ------------------------------ Bookings ------------------------------ */
    async renderBookings(flash) {
      const holder = h('div', null, this.skeleton(2));
      this.mount(this.shell('bookings', h('div', null, this.pageHead('My account', 'MY BOOKINGS', 'Trips and workshops you have added to your account.'), holder)));
      try {
        const data = await this.api('/api/account/bookings');
        const parts = [];
        if (flash) { const box = h('div', { class: 'cla-alert' }); this.showAlert(box, flash, 'ok'); parts.push(box); }
        if (!data.bookings.length) {
          parts.push(this.emptyCard('compass', 'YOUR NEXT EXPERIENCE STARTS HERE.', "You haven't added a booking yet. Already booked? Add it below with your booking reference — or discover something worth looking forward to.", { href: this.exploreUrl, label: 'EXPLORE EXPERIENCES' }));
          parts.push(this.linkCard(true));
        } else {
          parts.push(h('ul', { class: 'cla-list', role: 'list' }, data.bookings.map((b) => h('li', null, this.bookingCard(b)))));
          parts.push(this.linkCard(false));
        }
        holder.replaceChildren(...parts);
      } catch (e) {
        if (e.name === 'AbortError' || this.handleAuthError(e)) return;
        holder.replaceChildren(this.errorCard(e.message, () => this.renderBookings()));
      }
    }

    /** "Add a booking": the customer proves a booking is theirs with its reference + the mobile number used. */
    linkCard(open) {
      const alertBox = h('div', { class: 'cla-alert', hidden: true });
      const ref = this.field('booking-ref', 'Booking reference', { autocomplete: 'off', autocapitalize: 'characters', spellcheck: 'false', maxlength: '24', placeholder: 'CLP-261001-ABCDE' }, 'Shown on your booking confirmation.');
      const phone = this.field('booking-phone', 'Mobile number used for the booking', { type: 'tel', inputmode: 'tel', autocomplete: 'tel', maxlength: '20', placeholder: '98765 43210' });
      const btn = this.button('ADD BOOKING', { type: 'submit', arrow: true });
      btn.classList.remove('cla-btn--block');
      const form = h('form', { class: 'cla-link-form', novalidate: true }, alertBox, h('div', { class: 'cla-grid-2' }, ref.el, phone.el), h('div', { class: 'cla-form-actions' }, btn));
      const details = h('details', { class: 'cla-card cla-add', open: open || null },
        h('summary', { class: 'cla-add__summary' }, h('span', { class: 'cla-add__plus', 'aria-hidden': 'true', text: '+' }), h('span', { text: 'Add a booking to your account' })),
        form);
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (this.busy) return;
        this.showAlert(alertBox, '');
        const r = ref.input.value.trim().toUpperCase().replace(/\s+/g, '');
        const p = phone.input.value.replace(/\D/g, '');
        let bad = false;
        if (!/^CLP-\d{6}-[A-Z2-9]{5}$/.test(r)) { ref.set('Enter the reference exactly as shown, e.g. CLP-261001-ABCDE.'); bad = true; }
        if (p.length < 10) { phone.set('Enter the mobile number used when booking.'); bad = true; }
        if (bad) { (form.querySelector('[aria-invalid="true"]') || ref.input).focus(); return; }
        this.setBusy(btn, true);
        try {
          const data = await this.api('/api/account/bookings/link', { method: 'POST', body: { booking_ref: r, phone: phone.input.value } });
          this.customer = data.customer;
          this.renderBookings(`${data.booking.trip.title} has been added to your account.`);
        } catch (ex) {
          if (ex.name === 'AbortError' || this.handleAuthError(ex)) return;
          this.showServerError(ex, alertBox, { invalid_ref: ref, invalid_phone: phone });
        } finally {
          this.setBusy(btn, false);
        }
      });
      return details;
    }

    tripImage(handle, img) {
      if (!handle) return;
      let p = this.imageCache.get(handle);
      if (!p) {
        p = fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' })
          .then((r) => (r.ok ? r.json() : null))
          .then((prod) => {
            const src = prod && (prod.featured_image || (prod.images && prod.images[0]));
            if (!src) return null;
            const u = new URL(src.startsWith('//') ? `https:${src}` : src, location.origin);
            u.searchParams.set('width', '900');
            return u.href;
          })
          .catch(() => null);
        this.imageCache.set(handle, p);
      }
      p.then((src) => {
        if (!src || !img.isConnected) return;
        img.src = src;
        img.hidden = false;
      });
    }

    bookingCard(b) {
      const img = h('img', { alt: '', loading: 'lazy', decoding: 'async', hidden: true, width: '900', height: '600' });
      this.tripImage(b.trip.handle, img);
      const open = () => this.go('bookings', b.booking_ref);
      return h('article', { class: 'cla-card cla-booking' },
        h('div', { class: 'cla-booking__media' }, img),
        h('div', { class: 'cla-booking__body' },
          h('div', { class: 'cla-booking__top' },
            h('div', null,
              h('p', { class: 'cla-ref' }, h('span', { class: 'cla-sr', text: 'Booking reference ' }), b.booking_ref),
              h('h2', { class: 'cla-h3', text: b.trip.title })),
            h('div', { class: 'cla-badges' }, badge(b.status), badge(b.payment_status))),
          h('dl', { class: 'cla-meta' },
            this.meta('Booked on', fmtDate(b.created_at)),
            this.meta('Travel dates', b.trip.dates || '—'),
            this.meta('Travellers', String(b.seats))),
          h('div', { class: 'cla-booking__foot' },
            h('p', { class: 'cla-amount' }, h('small', { text: 'Amount paid' }), fmtMoney(b.amount_paid, b.currency)),
            h('a', { class: 'cla-btn cla-btn--ghost', href: `?tab=bookings&ref=${encodeURIComponent(b.booking_ref)}`, onclick: (e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); open(); } },
              h('span', { text: 'View details' }), h('span', { class: 'cla-sr', text: ` for ${b.trip.title}` }), icon('arrow')))));
    }

    meta(label, value) {
      return h('div', null, h('dt', { text: label }), h('dd', { text: value }));
    }

    async renderBookingDetail(ref) {
      const back = { href: '?tab=bookings', label: 'All bookings', go: () => this.go('bookings') };
      const holder = h('div', null, this.skeleton(3));
      this.mount(this.shell('bookings', h('div', null, this.pageHead('Booking details', ref, null, back), holder)));
      try {
        const { booking: b } = await this.api(`/api/account/bookings/${encodeURIComponent(ref)}`);
        holder.previousSibling.querySelector('.cla-h1').textContent = b.trip.title;
        const img = h('img', { alt: '', decoding: 'async', hidden: true, width: '1600', height: '600' });
        this.tripImage(b.trip.handle, img);

        const hero = h('div', { class: 'cla-card cla-hero-card' },
          h('div', { class: 'cla-hero-card__media' }, img),
          h('div', { class: 'cla-hero-card__body' },
            h('div', { class: 'cla-booking__top' },
              h('p', { class: 'cla-ref' }, 'Booking reference ', b.booking_ref),
              h('div', { class: 'cla-badges' }, badge(b.status), badge(b.payment_status))),
            h('dl', { class: 'cla-meta' },
              this.meta('Booked on', fmtDate(b.created_at)),
              this.meta('Travel dates', b.trip.dates || '—'),
              this.meta('Duration', b.trip.duration || '—'),
              this.meta('Destination', b.trip.destination || '—'),
              b.trip.variant && b.trip.variant !== 'Default Title' ? this.meta('Package', b.trip.variant) : null,
              this.meta('Travellers', String(b.seats)))));

        const booker = h('section', { class: 'cla-card cla-panel', 'aria-labelledby': 'cla-booker' },
          h('h2', { class: 'cla-h3', id: 'cla-booker', text: 'Primary booker' }),
          h('dl', { class: 'cla-meta' },
            this.meta('Name', b.booker.name), this.meta('Mobile', b.booker.phone), this.meta('Email', b.booker.email), this.meta('City', b.booker.city)));

        const travellers = h('section', { class: 'cla-card cla-panel', 'aria-labelledby': 'cla-trav' },
          h('h2', { class: 'cla-h3', id: 'cla-trav', text: 'Travellers' }),
          h('table', { class: 'cla-table' },
            h('thead', null, h('tr', null, h('th', { scope: 'col', text: 'Name' }), h('th', { scope: 'col', text: 'Age' }))),
            h('tbody', null, b.travellers.map((t, i) => h('tr', null, h('td', null, `${i + 1}. ${t.name}`), h('td', { text: String(t.age) }))))));

        const sumRow = (k, v, cls) => h('div', { class: cls || null }, h('dt', { text: k }), h('dd', { text: v }));
        const pay = b.payment;
        const payment = h('section', { class: 'cla-card cla-panel', 'aria-labelledby': 'cla-pay' },
          h('h2', { class: 'cla-h3', id: 'cla-pay', text: 'Payment summary' }),
          h('dl', { class: 'cla-sum' },
            sumRow(`${fmtMoney(b.price_per_person, b.currency)} × ${b.seats} traveller${b.seats > 1 ? 's' : ''}`, fmtMoney(b.total_amount, b.currency)),
            sumRow(b.payment_mode === 'advance' ? 'Advance paid' : 'Amount paid', fmtMoney(b.amount_paid, b.currency)),
            b.balance_due > 0 ? sumRow(b.balance_due_date ? `Balance due by ${fmtDate(b.balance_due_date)}` : 'Balance due', fmtMoney(b.balance_due, b.currency)) : null,
            pay && pay.reference ? sumRow('Payment reference', pay.reference) : null,
            pay && pay.method ? sumRow('Method', METHOD_LABEL[pay.method] || pay.method) : null,
            pay && pay.paid_at ? sumRow('Paid on', fmtDate(pay.paid_at)) : null,
            b.order_name ? sumRow('Order', b.order_name) : null,
            sumRow('Total', fmtMoney(b.total_amount, b.currency), 'is-total')));

        const actions = h('div', { class: 'cla-actions' },
          h('button', { type: 'button', class: 'cla-btn', onclick: () => this.downloadBooking(b) }, icon('download'), h('span', { text: 'Download details' })),
          this.whatsapp ? h('a', { class: 'cla-btn cla-btn--ghost', href: `https://wa.me/${this.whatsapp}?text=${encodeURIComponent(`Hi Club Laparoz! My booking reference is ${b.booking_ref}`)}`, target: '_blank', rel: 'noopener' }, icon('chat'), h('span', { text: 'WhatsApp us' })) : null,
          b.trip.handle ? h('a', { class: 'cla-btn cla-btn--ghost', href: `/products/${encodeURIComponent(b.trip.handle)}` }, h('span', { text: 'View trip' }), icon('arrow')) : null,
          this.supportEmail ? h('a', { class: 'cla-btn cla-btn--ghost', href: `mailto:${this.supportEmail}?subject=${encodeURIComponent(`Booking ${b.booking_ref}`)}` }, icon('mail'), h('span', { text: 'Email support' })) : null);

        holder.replaceChildren(h('div', { class: 'cla-detail' },
          hero,
          h('div', { class: 'cla-grid-2' }, booker, payment),
          travellers,
          h('section', { class: 'cla-card cla-panel', 'aria-label': 'Booking actions' },
            h('h2', { class: 'cla-h3', text: 'Booking actions' }),
            b.status === 'pending' ? h('p', { class: 'cla-alert cla-alert--info', text: 'This booking is waiting for payment confirmation. If you have already paid, it will update automatically within a few minutes.' }) : null,
            actions)));
      } catch (e) {
        if (e.name === 'AbortError' || this.handleAuthError(e)) return;
        holder.replaceChildren(this.errorCard(e.status === 404 ? 'We could not find this booking in your account.' : e.message, e.status === 404 ? null : () => this.renderBookingDetail(ref)));
      }
    }

    downloadBooking(b) {
      const L = [];
      L.push('CLUB LAPAROZ — BOOKING DETAILS', '');
      L.push(`Booking reference: ${b.booking_ref}`, `Status: ${STATUS_LABEL[b.status] || b.status}`, `Payment: ${STATUS_LABEL[b.payment_status] || b.payment_status}`, '');
      L.push(`Trip: ${b.trip.title}`);
      if (b.trip.destination) L.push(`Destination: ${b.trip.destination}`);
      if (b.trip.dates) L.push(`Dates: ${b.trip.dates}`);
      if (b.trip.duration) L.push(`Duration: ${b.trip.duration}`);
      if (b.trip.variant && b.trip.variant !== 'Default Title') L.push(`Package: ${b.trip.variant}`);
      L.push('', 'PRIMARY BOOKER', `${b.booker.name} · ${b.booker.phone} · ${b.booker.email} · ${b.booker.city}`);
      L.push('', 'TRAVELLERS', ...b.travellers.map((t, i) => `${i + 1}. ${t.name} (${t.age})`));
      L.push('', 'PAYMENT', `Total: ${fmtMoney(b.total_amount, b.currency)}`, `Paid: ${fmtMoney(b.amount_paid, b.currency)}`);
      if (b.balance_due > 0) L.push(`Balance due: ${fmtMoney(b.balance_due, b.currency)}`);
      if (b.payment && b.payment.reference) L.push(`Payment reference: ${b.payment.reference}`);
      L.push('', `Booked on ${fmtDate(b.created_at)}`);
      const blob = new Blob([L.join('\n')], { type: 'text/plain;charset=utf-8' });
      const a = h('a', { href: URL.createObjectURL(blob), download: `club-laparoz-${b.booking_ref}.txt` });
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 500);
    }

    /* ------------------------------ Payments ------------------------------ */
    async renderPayments() {
      const holder = h('div', null, this.skeleton(2));
      this.mount(this.shell('payments', h('div', null, this.pageHead('My account', 'PAYMENTS & SUBSCRIPTIONS', 'Verified payments from our secure payment partner, Razorpay.'), holder)));
      try {
        const data = await this.api('/api/account/payments');
        const parts = [];

        if (data.balances.length) {
          parts.push(h('section', { class: 'cla-section', 'aria-labelledby': 'cla-bal' },
            h('h2', { class: 'cla-h3', id: 'cla-bal', text: 'Outstanding balances' }),
            h('ul', { class: 'cla-pay-list', role: 'list' }, data.balances.map((x) => h('li', { class: 'cla-card cla-pay' },
              h('div', null,
                h('p', { class: 'cla-pay__title', text: x.trip_title }),
                h('p', { class: 'cla-pay__meta', text: `Total ${fmtMoney(x.total_amount, x.currency)} · Paid ${fmtMoney(x.amount_paid, x.currency)} · Ref ${x.booking_ref}` })),
              h('div', { class: 'cla-pay__right' },
                h('span', { class: 'cla-amount' }, h('small', { text: x.due_date ? `Due ${fmtDate(x.due_date)}` : 'Remaining' }), fmtMoney(x.balance_due, x.currency)),
                badge('partially_paid')))))));
        }

        parts.push(h('section', { class: 'cla-section', 'aria-labelledby': 'cla-hist' },
          h('h2', { class: 'cla-h3', id: 'cla-hist', text: 'Payment history' }),
          data.payments.length
            ? h('ul', { class: 'cla-pay-list', role: 'list' }, data.payments.map((p) => h('li', { class: 'cla-card cla-pay' },
              h('div', null,
                h('p', { class: 'cla-pay__title', text: p.trip_title }),
                h('p', { class: 'cla-pay__meta', text: [fmtDate(p.paid_at), METHOD_LABEL[p.method] || p.method || null, p.type === 'advance' ? 'Advance' : 'Full payment', `Txn ${p.reference}`].filter(Boolean).join(' · ') })),
              h('div', { class: 'cla-pay__right' },
                h('span', { class: 'cla-amount', text: fmtMoney(p.amount, p.currency) }),
                badge(p.status)))))
            : this.emptyCard('card', 'NO PAYMENTS YET', 'Payments for your trips and workshops will appear here once they are confirmed.', { href: this.exploreUrl, label: 'EXPLORE EXPERIENCES' })));

        parts.push(h('section', { class: 'cla-section', 'aria-labelledby': 'cla-subs' },
          h('h2', { class: 'cla-h3', id: 'cla-subs', text: 'Subscriptions' }),
          data.subscriptions.length
            ? h('ul', { class: 'cla-pay-list', role: 'list' }, data.subscriptions.map((s) => h('li', { class: 'cla-card cla-pay' },
              h('div', null,
                h('p', { class: 'cla-pay__title', text: s.name }),
                h('p', { class: 'cla-pay__meta', text: [s.renews_at ? `Renews ${fmtDate(s.renews_at)}` : null, s.cancellation_note || null].filter(Boolean).join(' · ') })),
              h('div', { class: 'cla-pay__right' }, h('span', { class: 'cla-amount', text: fmtMoney(s.amount, s.currency) }), badge(s.status)))))
            : h('div', { class: 'cla-card cla-empty' },
              h('span', { class: 'cla-empty__icon' }, icon('repeat')),
              h('h3', { class: 'cla-h3', text: 'No active subscriptions' }),
              h('p', { class: 'cla-muted', text: 'You have no subscriptions with CLUB LAPAROZ. Any subscription you start will be listed here with its renewal date and payment history.' }))));

        holder.replaceChildren(...parts);
      } catch (e) {
        if (e.name === 'AbortError' || this.handleAuthError(e)) return;
        holder.replaceChildren(this.errorCard(e.message, () => this.renderPayments()));
      }
    }

    /* ------------------------------ Profile ------------------------------ */
    renderProfile() {
      const c = this.customer;
      const alertBox = h('div', { class: 'cla-alert', hidden: true });
      const fName = this.field('name', 'Full name', { autocomplete: 'name', maxlength: '80', required: true, value: c.name || '' });
      const fEmail = this.field('email', 'Email address', { type: 'email', autocomplete: 'email', inputmode: 'email', maxlength: '120', required: true, value: c.email || '' }, 'You log in with this email.');
      const fPhone = this.field('phone', 'Mobile number', { type: 'tel', inputmode: 'tel', autocomplete: 'tel', maxlength: '20', placeholder: '+91 98765 43210', value: c.phone || '' }, 'Optional. Used by our team to reach you about your trips.');
      const fCity = this.field('city', 'City', { autocomplete: 'address-level2', maxlength: '60', value: c.city || '' });

      const save = this.button('SAVE CHANGES', { type: 'submit' });
      save.classList.remove('cla-btn--block');
      const cancel = h('button', { type: 'button', class: 'cla-btn cla-btn--ghost', text: 'Cancel' });
      const form = h('form', { class: 'cla-card cla-profile', novalidate: true },
        h('h2', { class: 'cla-h3 cla-form-title', text: 'Personal details' }),
        alertBox, fName.el, fEmail.el, fPhone.el, fCity.el,
        h('div', { class: 'cla-form-actions' }, save, cancel));

      const fields = { name: fName, email: fEmail, phone: fPhone, city: fCity };
      const clearErrors = () => Object.values(fields).forEach((f) => { f.input.removeAttribute('aria-invalid'); f.err.textContent = ''; });
      cancel.addEventListener('click', () => {
        fName.input.value = this.customer.name || '';
        fEmail.input.value = this.customer.email || '';
        fPhone.input.value = this.customer.phone || '';
        fCity.input.value = this.customer.city || '';
        clearErrors();
        this.showAlert(alertBox, '');
      });
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (this.busy) return;
        clearErrors();
        this.showAlert(alertBox, '');
        const body = { name: fName.input.value.trim(), email: fEmail.input.value.trim(), phone: fPhone.input.value.trim(), city: fCity.input.value.trim() };
        if (!body.name) { fName.set('Please enter your full name.'); fName.input.focus(); return; }
        if (!this.validEmail(body.email)) { fEmail.set('Please enter a valid email address.'); fEmail.input.focus(); return; }
        this.setBusy(save, true);
        try {
          const data = await this.api('/api/account/profile', { method: 'POST', body });
          this.customer = data.customer;
          fPhone.input.value = this.customer.phone || '';
          const who = this.querySelector('.cla-side__name');
          if (who) who.textContent = this.customer.name || 'CLUB LAPAROZ member';
          const mail = this.querySelector('.cla-side__phone');
          if (mail) mail.lastChild.textContent = ` ${this.customer.email}`;
          this.showAlert(alertBox, 'Your profile has been updated.', 'ok');
        } catch (ex) {
          if (ex.name === 'AbortError' || this.handleAuthError(ex)) return;
          this.showServerError(ex, alertBox, fields);
          if (ex.extra && ex.extra.fields) this.showAlert(alertBox, ex.message);
        } finally {
          this.setBusy(save, false);
        }
      });

      this.mount(this.shell('profile', h('div', null,
        this.pageHead('My account', 'MY PROFILE', 'Keep your details up to date for smoother bookings.'),
        h('div', { class: 'cla-profile-stack' }, form, this.passwordCard()))));
    }

    passwordCard() {
      const alertBox = h('div', { class: 'cla-alert', hidden: true });
      const cur = this.field('current-password', 'Current password', { type: 'password', autocomplete: 'current-password', maxlength: '128' });
      const next = this.field('new-password', 'New password', { type: 'password', autocomplete: 'new-password', maxlength: '128' }, 'At least 8 characters, with a letter and a number. You will be signed out on other devices.');
      const btn = this.button('UPDATE PASSWORD', { type: 'submit' });
      btn.classList.remove('cla-btn--block');
      const form = h('form', { class: 'cla-card cla-profile', novalidate: true },
        h('h2', { class: 'cla-h3 cla-form-title', text: 'Password' }), alertBox, cur.el, next.el,
        h('div', { class: 'cla-form-actions' }, btn));
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (this.busy) return;
        this.showAlert(alertBox, '');
        if (!cur.input.value) { cur.set('Please enter your current password.'); cur.input.focus(); return; }
        const pp = this.passwordProblem(next.input.value);
        if (pp) { next.set(pp); next.input.focus(); return; }
        this.setBusy(btn, true);
        try {
          const data = await this.api('/api/account/password', { method: 'POST', body: { current_password: cur.input.value, new_password: next.input.value } });
          if (data.token) this.setToken(data.token); // the old session was revoked with the password change
          cur.input.value = '';
          next.input.value = '';
          this.showAlert(alertBox, 'Your password has been updated. Other devices have been signed out.', 'ok');
        } catch (ex) {
          if (ex.name === 'AbortError' || this.handleAuthError(ex)) return;
          this.showServerError(ex, alertBox, { current_password: cur, weak_password: next });
        } finally {
          this.setBusy(btn, false);
        }
      });
      return form;
    }

    /* ------------------------------- Logout ------------------------------- */
    async logout() {
      if (this.busy) return;
      this.busy = true;
      let failed = false;
      try {
        await this.api('/api/auth/logout', { method: 'POST' });
      } catch (e) {
        if (e.name === 'AbortError') return;
        failed = e.status === 0;
      } finally {
        this.busy = false;
      }
      if (failed) {
        // Do not pretend: the session is still active on the server.
        const main = this.querySelector('.cla-main');
        if (main) {
          const box = h('div', { class: 'cla-alert' });
          this.showAlert(box, 'We could not reach the server to log you out. Please check your connection and try again.');
          main.prepend(box);
        }
        return;
      }
      // The server has revoked the session; drop everything held locally too.
      this.setToken('');
      this.customer = null;
      this.auth = null;
      this.imageCache.clear();
      this.setUrl({}, true);
      this.renderLogin({ alert: { type: 'ok', text: "You've been logged out." } });
      window.scrollTo({ top: 0 });
    }
  }

  window.customElements.define('cl-account', ClAccount);
})();
